import { atom } from 'jotai';

import {
  type AlertDeviceField,
  type BasicSourceLogTypeDTO,
  type BasicSourceTypeDTO,
  type ConnectionInputSchema,
  type LimaDevicesDTO,
  type SourceCreateBaseDTO,
  type SourceDetailResponse,
  SourceDialogMode
} from './SourceCreate.types';
import type { SupportedLogSourceModel } from './SourceInfoBlock/SourceInfoBlock.types';
import type { SelectedSource } from './SourceSelectionStep';

// ============================================================================
// SOURCE TYPE SELECTION & CONFIGURATION
// ============================================================================
// These atoms manage the source type catalog, schema fetching, and dynamic
// form field generation based on the selected source type.

/**
 * Full list of available source types (unfiltered).
 *
 * Loaded once on dialog mount from the backend `/source-types` endpoint.
 * Contains all source types regardless of whether they're available for
 * the current device or organization.
 */
export const originalSourceTypesAtom = atom<BasicSourceTypeDTO[]>([]);

/**
 * Filtered source types available for selection in the current context.
 *
 * **Filtering rules:**
 * - In New mode: All source types where `is_active: true`
 * - In Edit mode: Same as New, plus the currently selected source type
 *   (even if marked inactive, to allow editing existing sources)
 */
export const sourceTypeBasicAtom = atom<BasicSourceTypeDTO[]>([]);

/**
 * Hierarchical grouping of source types by vendor/category.
 *
 * **Structure:** Source types organized into collapsible vendor groups
 * (e.g., "Microsoft" → [O365, Defender, Entra ID]).
 *
 * **Usage:** Powers the tiered selection UI in the source type dropdown.
 */
export const tieredSourceTypesAtom = atom<BasicSourceTypeDTO[]>([]);

/**
 * Source type ID 24 represents the LogSources source type.
 *
 * The associated module configuration JSON contains the available log sources
 * for the log forwarder devices and their respective streams.
 */
export const selectedSourceTypeIdAtom = atom(24);

/**
 * Transient flag indicating a source type change is in progress.
 *
 * **Purpose:** Prevents race conditions during source type changes.
 *
 * **Prevents:**
 * - Stale form fields from old schema rendering before cleanup
 * - User interactions during the reset/refetch cycle
 * - Double-submission during cleanup
 *
 * **Set by:** `useSourceTypeManager` hook during source type transitions
 */
export const isResettingSourceTypeAtom = atom(false);

/**
 * Dynamic form schema for the currently selected source type.
 *
 * **Content:** JSON schema defining:
 * - Dynamic form fields (text inputs, dropdowns, checkboxes)
 * - Client secret vs certificate authentication fields
 * - Data provider configuration (for container-based sources)
 * - Validation rules and field dependencies
 * - UI hints (field labels, help text, groupings)
 *
 * **Source:** Fetched from backend when `selectedSourceTypeIdAtom` changes.
 *
 * **Critical:** This is the single source of truth for what fields to render.
 */
export const connectionInputSchemaAtom = atom<ConnectionInputSchema>({});

// ============================================================================
// LOG TYPE & CATEGORY MANAGEMENT
// ============================================================================
// These atoms handle log type filtering, category selection, and Microsoft
// Defender's complex category-to-subscription-type mapping.

/**
 * All available categories/log types for the selected source type.
 *
 * **Usage:**
 * - Microsoft sources: Categories filter which logs to ingest (e.g., "Exchange Online", "SharePoint")
 * - AWS sources: Log types available for selection (e.g., "CloudTrail", "VPCFlowLogs")
 *
 * **Source:** Fetched from `/categories?source_type_id={id}` endpoint
 */
export const categoryAtom = atom<BasicSourceLogTypeDTO[]>([]);

/**
 * Full list of source-specific log types for validation purposes.
 *
 * **Why separate from categoryAtom:**
 * Some sources distinguish between "categories" (user-facing groupings) and
 * "log_types" (backend ingestion types). This atom stores the raw log type list.
 */
export const sourceLogTypeAtom = atom<BasicSourceLogTypeDTO[]>([]);

/**
 * Categories available specifically for Microsoft data providers.
 *
 * **Microsoft complexity:**
 * - Parent source has categories (e.g., Exchange, SharePoint, Teams)
 * - Data providers (O365, Defender, GraphAPI, O365API) may have different categories
 * - Defender has special category filtering based on subscription type
 *
 * **Usage:** Populated when Microsoft source type is selected
 */
export const microsoftDataProvidersCategoriesAtom = atom<
  BasicSourceLogTypeDTO[]
>([]);

/**
 * User-selected categories for the current source configuration.
 *
 * **Behavior by mode:**
 * - New mode: Empty array, user selects from available categories
 * - Edit mode: Pre-populated with existing source's categories
 *
 * **Special handling:**
 * - Microsoft Defender: Category affects available subscription types
 * - Some sources: Mandatory categories (at least one must be selected)
 */
export const selectedCategoryAtom = atom<string[]>([]);

// ============================================================================
// MICROSOFT-SPECIFIC CONFIGURATION
// ============================================================================
// Microsoft sources have unique configuration requirements including
// subscription types, polling intervals, and API-specific settings.

/**
 * Microsoft subscription type (license level).
 *
 * **Valid values:**
 * - E3: Basic license tier
 * - E5: Advanced license tier (more log types available)
 * - Business/Enterprise plans: Various SKU options
 *
 * **Impact:**
 * - Determines which log categories are available
 * - Affects API endpoints and authentication scopes
 */
export const selectedSubscriptionTypeAtom = atom('');

/**
 * Polling interval for Microsoft API calls (parent source level).
 *
 * **Valid values:** "15m", "30m", "1h", "4h", "12h", "24h"
 *
 * **Business rule:**
 * More frequent polling (e.g., 15m) provides lower latency but increases API costs.
 * Recommendation: 1h for most deployments.
 */
export const selectedTimeIntervalAtom = atom('');

/**
 * Polling interval for Microsoft data providers (sub-source level).
 *
 * **Why separate from selectedTimeIntervalAtom:**
 * Some Microsoft sources allow per-data-provider polling intervals.
 * Example: O365 provider polls every 15m, Defender polls every 1h.
 */
export const selectedSubTimeIntervalAtom = atom('');

/**
 * Available time interval options based on current source configuration.
 *
 * **Dynamic population:**
 * Options change based on subscription type and selected categories.
 * Some high-frequency intervals are only available for E5 licenses.
 */
export const subTimeIntervalOptionsAtom = atom<string[]>([]);

// ============================================================================
// CLOUD PROVIDER & REGIONAL CONFIGURATION
// ============================================================================
// These atoms manage cloud provider selection (AWS, Azure, GCP) and
// region-specific settings for multi-region sources.

/**
 * Selected cloud provider(s) for multi-cloud sources.
 *
 * **Usage:**
 * Some source types support multiple cloud providers simultaneously.
 * Example: CrowdStrike can ingest from AWS, Azure, and GCP endpoints.
 *
 * **Format:** Array of provider names (e.g., ["AWS", "Azure"])
 */
export const selectedCloudProviderAtom = atom<string[]>([]);

/**
 * Selected AWS regions for region-scoped sources.
 *
 * **Usage:**
 * AWS-based sources (S3, CloudTrail, GuardDuty) can filter by region.
 * User selects which regions to ingest from.
 *
 * **Format:** Array of AWS region codes (e.g., ["us-east-1", "us-west-2"])
 */
export const selectedRegionAtom = atom<string[]>([]);

// ============================================================================
// VULNERABILITY & ALERT CONFIGURATION
// ============================================================================
// These atoms manage device vulnerability scanning and alert field mappings
// for sources that support asset/vulnerability ingestion.

/**
 * Selected vulnerability fields to ingest from the source.
 *
 * **Usage:**
 * Sources like Qualys, Tenable, and Rapid7 provide vulnerability data.
 * User selects which fields to import (e.g., CVE ID, CVSS score, remediation).
 */
export const selectedVulnFieldsAtom = atom<string[]>([]);

/**
 * Alert-to-device field mappings for asset correlation.
 *
 * **Purpose:**
 * Maps alert fields (e.g., `source.ip`, `device.hostname`) to device identifiers
 * for correlating alerts with the asset inventory.
 *
 * **Structure:** Array of `{ key, category }` objects defining the mappings
 */
export const deviceAlertFieldsAtom = atom<AlertDeviceField[]>([]);

// ============================================================================
// SUB-SOURCE & NESTED CONFIGURATION
// ============================================================================
// Some source types have sub-types or nested configurations requiring
// additional UI state management.

/**
 * Selected sub-source type for hierarchical sources.
 *
 * **Usage:**
 * Some sources have multiple ingestion methods or sub-types:
 * - Syslog: TCP vs UDP vs TLS
 * - API-based: Polling vs Webhook vs Streaming
 *
 * **Undefined by default:** Only populated for sources with sub-types
 */
export const selectedSubSourceTypeAtom = atom<string>();

/**
 * Active tab in the ingestion configuration section.
 *
 * **Tabs:**
 * - "ingest_method_config": Primary ingestion settings (API keys, credentials)
 * - "data_provider": Container-based ingestion (S3/GCS data providers)
 *
 * **Only visible when:** `is_container_ingest_enabled: true` in schema
 */
export const selectedIngestConfigTabAtom = atom<string>('ingest_method_config');

/**
 * Shows info icon when Microsoft providers have conflicting ingest_from dates.
 *
 * **Business rule:**
 * In Edit mode, if Microsoft data providers have different historical start dates,
 * display an info icon warning the user about the discrepancy.
 *
 * **Trigger:** Set by `useIngestFromInheritance` hook when date conflict detected
 */
export const showInfoIconCommonIngestFromAtom = atom<boolean>(false);

// ============================================================================
// REFERENCE DATA & LOOKUP LISTS
// ============================================================================
// These atoms cache reference data loaded once per dialog lifecycle.

/**
 * List of log sources supported by the selected source type.
 *
 * **Usage:**
 * Displayed in the "Supported Log Sources" information block to help users
 * understand what data will be ingested.
 *
 * **Example:** AWS CloudTrail supports: Management Events, Data Events, Insights
 */
export const supportedLogSourcesAtom = atom<SupportedLogSourceModel[]>([]);

/**
 * Available LIMA devices for device-source association.
 *
 * **Usage:**
 * When creating a source, user can associate it with a LIMA device (agent).
 * This atom provides the dropdown options.
 *
 * **Source:** Fetched from `/lima-devices` endpoint
 */
export const limaDevicesAtom = atom<LimaDevicesDTO[]>([]);

// ============================================================================
// DIALOG STATE & MODE MANAGEMENT
// ============================================================================
// These atoms control the dialog's open/closed state, New vs Edit mode,
// and store the data for the current operation.

/**
 * Controls whether the source create/edit dialog is open.
 *
 * **Lifecycle:**
 * - Opens: When user clicks "Add Source" or "Edit" on existing source
 * - Closes: On successful submission, cancel, or ESC key
 */
export const sourceDialogOpenAtom = atom(false);

/**
 * Current dialog mode: 'New' or 'Edit'.
 *
 * **Mode differences:**
 * - **New:** Empty form, all fields editable, inheritance hooks apply
 * - **Edit:** Pre-populated form, some fields read-only, preserve existing config
 *
 * **Critical for:** Conditional field rendering, inheritance logic, validation rules
 */
export const sourceDialogModeAtom = atom<SourceDialogMode>(
  SourceDialogMode.New
);

/**
 * Full source details when in Edit mode.
 *
 * **Purpose:**
 * Stores the complete source object fetched from backend, including:
 * - All form fields
 * - Connection inputs and credentials
 * - Data provider configurations
 * - Historical ingestion settings
 *
 * **Empty in New mode:** Only populated when editing existing source
 */
export const selectedSourceDetailsAtom = atom<SourceDetailResponse>(
  {} as SourceDetailResponse
);

/**
 * Source creation/update request payload being constructed.
 *
 * **Lifecycle:**
 * - Populated incrementally as user fills the form
 * - Validated before submission
 * - Sent to backend on final submit
 */
export const sourceCreateRequestAtom = atom<SourceCreateBaseDTO>(
  {} as SourceCreateBaseDTO
);

/**
 * Registered Acronis credential validator used during submit-time checks.
 *
 * The validator is provided by the Acronis-specific topics form so the
 * generic source submission flow can verify changed credentials before save.
 */
export const acronisTopicsValidatorAtom = atom<null | (() => Promise<boolean>)>(
  null
);

/**
 * Response from successful source creation or update.
 *
 * **Usage:**
 * - New mode: Contains newly created source_uid and other IDs
 * - Edit mode: Contains updated source details
 * - Used for success messaging and post-submission navigation
 */
export const sourceResponseAtom = atom<SourceCreateBaseDTO>(
  {} as SourceCreateBaseDTO
);

// ============================================================================
// AUTHENTICATION & CERTIFICATE MANAGEMENT
// ============================================================================
// Microsoft sources support two authentication methods: client secrets and
// certificate-based authentication. These atoms manage that workflow.

/**
 * Selected authentication method for Microsoft sources.
 *
 * **Values:**
 * - "client_secret": Use Azure App Registration client secret (simpler, less secure)
 * - "certificate": Use X.509 certificate (more secure, requires cert management)
 *
 * **Default:** "client_secret" for ease of setup
 */
export const authenticationModeAtom = atom<string>('client_secret');

/**
 * User wants to generate a new certificate (vs upload existing).
 *
 * **Certificate workflow:**
 * - `newCertificateAtom = true`: Platform generates cert and provides download
 * - `newCertificateAtom = false`: User uploads existing cert thumbprint
 */
export const newCertificateAtom = atom(false);

/**
 * User wants to regenerate certificate for existing Microsoft source.
 *
 * **Edit mode only:**
 * When editing a source using certificate auth, allows regenerating the cert
 * (useful when cert is expiring or compromised).
 */
export const regenerateCertificateAtom = atom(false);

/**
 * Status of certificate thumbprint validation.
 *
 * **Values:** "success", "error", or empty string
 *
 * **Usage:**
 * When user provides existing certificate thumbprint, backend validates it.
 * This atom stores validation result to show success/error UI states.
 */
export const thumbPrintIdStatusAtom = atom('');

// ============================================================================
// WIZARD NAVIGATION (ALTERNATE FLOW)
// ============================================================================
// Some sources use a multi-step wizard instead of a single dialog form.
// These atoms manage wizard-specific state.

/**
 * Current step index in the wizard flow (0-based).
 *
 * **Wizard steps:**
 * 1. Step 0: Source type selection
 * 2. Step 1: Configuration
 * 3. Step 2: Review and submit
 *
 * **Usage:** Determines which wizard panel to render
 */
export const wizardCurrentStepAtom = atom<number>(0);

/**
 * Selected source in the wizard's source selection step.
 *
 * **Structure:**
 * Contains source type metadata selected from the visual picker.
 *
 * **Null when:** Wizard is closed or source not yet selected
 */
export const wizardSelectedSourceAtom = atom<SelectedSource | null>(null);
